import { Badge, Button, Drawer, Flex, Text } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { setInvestmentTargetDates } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import {
	DATE_FORMATS,
	formatDateToBEIgnoreTimezone,
	formatDateToUI,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput.tsx';
import { useMutation } from '@tanstack/react-query';

import type { InvestmentTargetDatesFormValues } from './InvestmentTargetDatesDrawer.types.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InvestmentTargetDatesDrawerStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentTargetDatesDrawer/InvestmentTargetDatesDrawer.styles.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	currentTargetStartDate: string | null;
	currentTargetCompletionDate: string | null;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const buildSchema = (
	tMipManage: ReturnType<typeof useTranslationMipManage>
): z.ZodType<InvestmentTargetDatesFormValues> =>
	z
		.object({
			targetStartDate: z.date().nullable(),
			targetCompletionDate: z.date().nullable(),
		})
		.refine(
			(data) =>
				!data.targetStartDate ||
				!data.targetCompletionDate ||
				data.targetStartDate.getTime() <= data.targetCompletionDate.getTime(),
			{
				message: tMipManage('investment_target_dates.drawer.inverted_range_error'),
				path: ['targetStartDate'],
			}
		);

export const InvestmentTargetDatesDrawer = ({
	opened,
	onClose,
	currentTargetStartDate,
	currentTargetCompletionDate,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { mipId, investmentId } = useRequiredParams('mipId', 'investmentId');
	const { refetch: refetchSummary } = useInvestmentDetailsSummary();
	const setTargetDatesRequest = useMutation({ mutationFn: setInvestmentTargetDates });

	const schema = useMemo(() => buildSchema(tMipManage), [tMipManage]);

	const initialValues = useMemo<InvestmentTargetDatesFormValues>(
		() => ({
			targetStartDate: currentTargetStartDate ? new Date(currentTargetStartDate) : null,
			targetCompletionDate: currentTargetCompletionDate
				? new Date(currentTargetCompletionDate)
				: null,
		}),
		[currentTargetStartDate, currentTargetCompletionDate]
	);

	const form = useForm<InvestmentTargetDatesFormValues>({
		resolver: zodResolver(schema),
		defaultValues: initialValues,
	});

	useEffect(() => {
		if (opened) {
			form.reset(initialValues);
		}
	}, [opened, initialValues, form]);

	const watchedStart = form.watch('targetStartDate');
	const watchedCompletion = form.watch('targetCompletionDate');

	const previewDays =
		watchedStart && watchedCompletion
			? Math.max(
					0,
					Math.round((watchedCompletion.getTime() - watchedStart.getTime()) / MS_PER_DAY)
				)
			: 0;

	const onSubmit = (values: InvestmentTargetDatesFormValues) => {
		setTargetDatesRequest.mutate(
			{
				mipId,
				investmentId,
				payload: {
					targetStartDate: values.targetStartDate
						? formatDateToBEIgnoreTimezone(values.targetStartDate)
						: null,
					targetCompletionDate: values.targetCompletionDate
						? formatDateToBEIgnoreTimezone(values.targetCompletionDate)
						: null,
				},
			},
			{
				onSuccess: () => {
					void refetchSummary();
					showToastV2({
						title: tMipManage('investment_target_dates.drawer.save_success'),
						state: 'success',
					});
					onClose();
				},
			}
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investment_target_dates.drawer.title')}
			styles={{
				header: {
					paddingBottom: '0.5rem',
				},
			}}
			size="484px"
		>
			<FormProvider {...form}>
				<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
					<InvestmentTargetDatesDrawerStyles>
						<Text c="dark.0" mb="2rem">
							{tMipManage('investment_target_dates.drawer.subtitle')}
						</Text>

						<MantineDateInput
							name="targetStartDate"
							label={tMipManage('investment_target_dates.drawer.start_label')}
							placeholder={tMipManage('investment_target_dates.drawer.choose_date')}
							size="md"
							leftSection={<Icon variant="calendar-v3" size="lg" color="black" />}
							clearable
							mb="1rem"
						/>

						<MantineDateInput
							name="targetCompletionDate"
							label={tMipManage('investment_target_dates.drawer.completion_label')}
							placeholder={tMipManage('investment_target_dates.drawer.choose_date')}
							size="md"
							leftSection={<Icon variant="calendar-v3" size="lg" color="black" />}
							clearable
						/>

						<Flex direction="column" gap="0.5rem" className="selected-dates-preview">
							<Text fz={14} fw={600} c="dark.0" mb="0.75rem">
								{tMipManage('investment_target_dates.drawer.preview_label')}
							</Text>

							<Flex gap="0.5rem" justify="center" align="center">
								<Text fw={600} c="dark.8" className="date-text" ta="right">
									{watchedStart &&
										formatDateToUI(
											watchedStart,
											DATE_FORMATS.DATE_FULL_MONTH_YEAR
										)}
								</Text>

								<Flex
									gap="0.25rem"
									justify="center"
									align="center"
									className="days-container"
								>
									<div className="separator" aria-hidden="true" />

									<Badge
										color="gray"
										variant="outline"
										radius="xl"
										size="sm"
										c="dark.4"
										className="badge"
										fw={400}
									>
										{tMipManage('investment_target_dates.drawer.preview_days', {
											days: previewDays,
										})}
									</Badge>

									<div className="separator" aria-hidden="true" />
								</Flex>

								<Text fw={600} c="dark.8" className="date-text" ta="left">
									{watchedCompletion &&
										formatDateToUI(
											watchedCompletion,
											DATE_FORMATS.DATE_FULL_MONTH_YEAR
										)}
								</Text>
							</Flex>
						</Flex>

						<Button
							fullWidth
							mt="auto"
							type="submit"
							loading={setTargetDatesRequest.isPending}
							fw={600}
						>
							{tMipManage('investment_target_dates.drawer.save_cta')}
						</Button>

						<Button
							variant="subtle"
							fullWidth
							loading={setTargetDatesRequest.isPending}
							c="dark.8"
							fw={600}
							onClick={onClose}
						>
							{tMipManage('investment_target_dates.drawer.cancel_cta')}
						</Button>
					</InvestmentTargetDatesDrawerStyles>
				</form>
			</FormProvider>
		</Drawer>
	);
};
